import { useTranslation } from "react-i18next";
import { ViewerFragmentCommonProps } from "../ViewerFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import LoadingViewerFragment from "../LoadingViewerFragment/LoadingViewerFragment";
import CountedProgress from "@coworker/apprestructured/src/task-management/components/CountedProgress/CountedProgress";
import { isNumber } from "@coworker/apprestructured/src/shared/helpers/number.helper";

export type ProgressViewerFragmentConfigProps = {
  title: string;
  total: string;
  current: string;
};

type ProgressViewerFragmentProps = {
  configProps: ProgressViewerFragmentConfigProps;
  commonProps: ViewerFragmentCommonProps;
};

function ProgressViewerFragment({
  configProps,
  commonProps,
}: ProgressViewerFragmentProps) {
  const { t } = useTranslation();
  const { task } = commonProps;
  const { title, total: totalField, current: currentField } = configProps;

  if (isLoading(task)) {
    return <LoadingViewerFragment />;
  }

  // Props `total` and `current` are dot-paths (e.g. "details.progress.total"), not values.
  // The `progress` object is absent/null until the testbuy is started, so guard the parent
  // object before reading the nested properties as `getByPath` throws if it has to traverse
  // through null/undefined.
  const progressPath = totalField.slice(0, totalField.lastIndexOf("."));
  const progress = getByPath(task, progressPath);

  if (!progress) {
    return null;
  }

  const totalValue = getByPath(task, totalField) as number | undefined;
  const currentValue = getByPath(task, currentField) as number | undefined;

  if (!isNumber(totalValue) || !isNumber(currentValue)) {
    return null;
  }

  return (
    <CountedProgress
      max={totalValue}
      count={currentValue}
      label={t(title)}
      colorByCompletion
    />
  );
}

export default ProgressViewerFragment;
